'use client';

import { useState, useEffect, useSyncExternalStore } from 'react';

const emptySubscribe = () => () => {};

const readLocalConsent = () =>
  document.cookie
    .split('; ')
    .find((row) => row.startsWith('localConsent='))
    ?.split('=')[1] === 'true';

export const useConsentManagement = () => {
  // Read the consent cookie via useSyncExternalStore so we never call setState
  // inside an effect. Server render reports "not loaded / no consent"; the
  // client reports the real cookie value after hydration.
  const consentGiven = useSyncExternalStore(
    emptySubscribe,
    readLocalConsent,
    () => false,
  );
  const consentLoaded = useSyncExternalStore(
    emptySubscribe,
    () => true,
    () => false,
  );

  return { consentGiven, consentLoaded };
};

export const useUserInteraction = () => {
  const [userInteracted, setUserInteracted] = useState(false);

  useEffect(() => {
    // Don't add listeners if already interacted
    if (userInteracted) {
      return;
    }

    const handleInteraction = () => {
      setUserInteracted(true);
      // Remove all listeners
      document.removeEventListener('click', handleInteraction);
      document.removeEventListener('scroll', handleInteraction);
      document.removeEventListener('touchstart', handleInteraction);
      document.removeEventListener('keydown', handleInteraction);
    };

    // Add interaction listeners with passive option for better performance
    document.addEventListener('click', handleInteraction, {
      passive: true,
      once: true,
    });
    document.addEventListener('scroll', handleInteraction, {
      passive: true,
      once: true,
    });
    document.addEventListener('touchstart', handleInteraction, {
      passive: true,
      once: true,
    });
    document.addEventListener('keydown', handleInteraction, {
      passive: true,
      once: true,
    });

    return () => {
      document.removeEventListener('click', handleInteraction);
      document.removeEventListener('scroll', handleInteraction);
      document.removeEventListener('touchstart', handleInteraction);
      document.removeEventListener('keydown', handleInteraction);
    };
  }, [userInteracted]);

  return userInteracted;
};
